<script setup lang="ts">
import { stringToFaDateTime } from '@payjib/shared'
import { computed, ref } from 'vue'
import {
  addTransactionNoteAction,
  getTransactionNotesAction,
} from '@/api/functions/transactions/get-transaction-details.action'
import { useMutationApi, useQueryApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import SectionHeader from './SectionHeader.vue'

const props = defineProps({
  transactionId: {
    type: String,
  },
  refetchTransaction: {
    type: Function,
    default: () => {},
  },
})

const toast = useToast()
const note = ref('')

const { data: notesResponse, refetch: refetchNotes } = useQueryApi({
  queryKey: ['transactionNotes', props.transactionId],
  queryFn: () => {
    if (!props.transactionId) {
      throw new Error('شناسه تراکنش نامعتبر است')
    }
    return getTransactionNotesAction(props.transactionId)
  },
  staleTime: 1000 * 30,
})

const notes = computed(() => notesResponse.value?.data || [])

const addNoteMutation = useMutationApi({
  mutationFn: (content: string) => {
    if (!props.transactionId) {
      throw new Error('شناسه تراکنش نامعتبر است')
    }
    return addTransactionNoteAction(props.transactionId, { content })
  },

  onSuccess: () => {
    toast.success('یادداشت با موفقیت ثبت شد', 'موفقیت')
    note.value = ''
    refetchNotes()
  },

  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const submitNote = () => {
  if (!note.value.trim()) {
    toast.error('متن یادداشت نمی‌تواند خالی باشد')
    return
  }

  addNoteMutation.mutate(note.value)
}

const reload = async () => {
  await refetchNotes()
}

defineExpose({
  reload,
})
</script>

<template>
  <section class="mt-10">
    <SectionHeader title="یادداشت‌ها" />

    <div class="grid gap-6 lg:grid-cols-2 items-start">
      <!-- History -->
      <div class="flex flex-col rounded-xl border border-[#d8d8d8] bg-white p-5 lg:p-6">
        <h3 class="mb-4 text-sm font-semibold text-[#303b4f]">تاریخچه یادداشت‌ها</h3>

        <div v-if="notes.length" class="flex flex-col divide-y divide-[#eaeef5]">
          <div
            v-for="note in notes"
            :key="note.id"
            class="flex flex-col gap-1.5 py-4 first:pt-0 last:pb-0"
          >
            <div class="flex items-center justify-between gap-3">
              <span class="text-sm font-semibold text-[#1b325a]">{{ note?.admin?.fullName }}</span>
              <span dir="ltr" class="shrink-0 text-xs text-gray-400 font-fa">{{
                stringToFaDateTime(note?.createdAt)
              }}</span>
            </div>
            <p class="text-sm leading-6 text-[#4e648c]">{{ note?.content }}</p>
          </div>
        </div>

        <div v-else class="py-12 text-center text-sm text-gray-400">یادداشتی ثبت نشده است</div>
      </div>

      <!-- New note -->
      <div class="flex flex-col rounded-xl border border-[#d8d8d8] bg-white p-5 lg:p-6">
        <h3 class="mb-4 text-sm font-semibold text-[#303b4f]">ثبت یادداشت جدید</h3>
        <Textarea v-model="note" class="min-h-[130px]" />
        <div class="mt-4 flex justify-end">
          <Button
            :isAdmin="true"
            @click="submitNote"
            :loading="addNoteMutation.isPending.value"
            :disabled="!note"
            class="h-10! px-5! rounded-[10px]! py-0! text-[13px] font-medium"
          >
            ارسال یادداشت
          </Button>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped></style>
